import React, { useContext, useEffect, useState } from "react";
import { TranslatorContext } from "../../../context/Translator";
import { Row, Col, Card, Modal, Form, Tab, Tabs, Table, Alert, Badge } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { LabelFieldComponent, LabelTextareaComponent } from "../../../components/fields";
import { ButtonComponent } from "../../../components/elements";
import ExchangeModal from "./modals/ExchangeModal";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";

export default function SaleEditPage() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();
    const { id } = useParams();
    const AuthToken = localStorage.getItem("authToken");

    const getTodayDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const formatPaymentDate = (dateString) => {
        if (!dateString) return getTodayDate();
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        }
        return getTodayDate();
    };

    const formatAmount = (amount) => {
        const num = Number(amount);
        if (Number.isInteger(num)) {
            return num.toString();
        }
        return num.toFixed(2);
    };

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [availableProducts, setAvailableProducts] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [errors, setErrors] = useState({});
    const [submitError, setSubmitError] = useState(null);
    const [initialSale, setInitialSale] = useState(null);
    const [existingPaymentsTotal, setExistingPaymentsTotal] = useState(0);
    const [activeTab, setActiveTab] = useState("edit");
    const [saleReturns, setSaleReturns] = useState([]);

    // Return States
    const [showReturnModal, setShowReturnModal] = useState(false);
    const [returnProducts, setReturnProducts] = useState([]);
    const [returnSubmitting, setReturnSubmitting] = useState(false);
    const [returnDate, setReturnDate] = useState(getTodayDate());
    const [returnReason, setReturnReason] = useState('');
    const [totalRefund, setTotalRefund] = useState(0);

    // Exchange States - Keep only the show/hide state
    const [showExchangeModal, setShowExchangeModal] = useState(false);

    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];
    const hasPermission = (perm) => permissions.includes(perm);
    const canEditSaleDate = hasPermission("Sales date edit");

    // Customer Modal States
    const [showCustomerModal, setShowCustomerModal] = useState(false);
    const [customerForm, setCustomerForm] = useState({
        name: '', email: '', phone: '', address: '', img: null
    });
    const [customerErrors, setCustomerErrors] = useState({});
    const [customerSubmitting, setCustomerSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        customer_id: "",
        sale_number: "",
        sale_date: getTodayDate(),
        notes: [],
        products: [],
        payments: [],
    });

    useEffect(() => {
        fetchSaleData();
    }, [id]);

    const fetchSaleData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/sales/${id}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            const data = response.data;
            setCustomers(data.customers || []);
            setAvailableProducts(data.products || []);
            setPaymentMethods(data.payment_methods || []);
            setSaleReturns(data.sale.return || []);

            const sale = data.sale;
            setInitialSale(sale);

            let productsArray = [];
            if (sale.details && sale.details.length > 0) {
                productsArray = sale.details.map(detail => ({
                    product_id: detail.product_id,
                    quantity: detail.quantity,
                    sale_price: parseFloat(detail.sale_price),
                    discount_price: parseFloat(detail.discount_price) || 0,
                    extra_price: parseFloat(detail.extra_price) || 0,
                    product_name: detail.product_name,
                }));
            }

            let existingTotal = 0;
            let paymentsArray = [];
            if (sale.payments && sale.payments.length > 0) {
                paymentsArray = sale.payments.map(payment => {
                    const amount = parseFloat(payment.amount);
                    const isRefund = amount < 0;

                    if (amount > 0) {
                        existingTotal += amount;
                    }

                    // Process child payments if they exist
                    let childPayments = [];
                    if (payment.child_payments && payment.child_payments.length > 0) {
                        childPayments = payment.child_payments.map(child => ({
                            id: child.id,
                            amount: parseFloat(child.amount),
                            payment_method_id: child.payment_method_id,
                            payment_date: formatPaymentDate(child.payment_date),
                            model: child.model || null,
                            isRefund: parseFloat(child.amount) < 0,
                        }));
                    }

                    let parentPayment = null;
                    if (payment.parent_payment) {
                        parentPayment = {
                            id: payment.parent_payment.id,
                            amount: parseFloat(payment.parent_payment.amount),
                            payment_method_id: payment.parent_payment.payment_method_id,
                            payment_date: formatPaymentDate(payment.parent_payment.payment_date),
                            model: payment.parent_payment.model || null,
                        };
                    }

                    return {
                        id: payment.id,
                        payment_method_id: payment.payment_method_id,
                        amount: amount,
                        payment_date: formatPaymentDate(payment.payment_date),
                        isExisting: true,
                        isRefund: isRefund,
                        child_payments: childPayments,
                        parent_payment: parentPayment,
                    };
                });
            }

            setExistingPaymentsTotal(existingTotal);

            let notesArray = [];
            if (sale.notes) {
                if (typeof sale.notes === "string") {
                    try {
                        const parsed = JSON.parse(sale.notes);
                        notesArray = Array.isArray(parsed) ? parsed : [sale.notes];
                    } catch {
                        notesArray = [sale.notes];
                    }
                } else if (Array.isArray(sale.notes)) {
                    notesArray = sale.notes;
                } else {
                    notesArray = [String(sale.notes)];
                }
            }

            setFormData({
                customer_id: sale.customer_id || "",
                sale_number: sale.sale_number || "",
                sale_date: sale.sale_date || getTodayDate(),
                notes: notesArray,
                products: productsArray,
                payments: paymentsArray,
            });
        } catch (err) {
            console.error("Error fetching sale data:", err);
            Swal.fire({
                icon: 'error',
                title: t("Error"),
                text: t("Failed to load sale data"),
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchCustomers = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/customers`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );
            setCustomers(response.data.customers || []);
        } catch (err) {
            console.error(err);
        }
    };

    const getTotalReturnedAmount = () => {
        return saleReturns.reduce((sum, ret) => sum + (parseFloat(ret.total_amount) || 0), 0);
    };

    const getNetSoldAmount = () => {
        return grandTotal - getTotalReturnedAmount();
    };

    // RETURN FUNCTIONS
    const handleOpenReturnModal = () => {
        const remainingProducts = formData.products.map(product => {
            const returnedQty = saleReturns
                .filter(ret => ret.product_id === product.product_id)
                .reduce((sum, ret) => sum + ret.quantity, 0);

            return {
                product_id: product.product_id,
                product_name: product.product_name,
                max_quantity: product.quantity,
                quantity: 0,
                sale_price: product.sale_price,
                discount_price: product.discount_price,
                extra_price: product.extra_price,
            };
        }).filter(p => p.max_quantity > 0);

        setReturnProducts(remainingProducts);
        setTotalRefund(0);
        setReturnDate(getTodayDate());
        setReturnReason('');
        setShowReturnModal(true);
    };

    const updateReturnQuantity = (index, quantity) => {
        const updated = [...returnProducts];
        const qty = Math.min(Math.max(0, parseInt(quantity) || 0), updated[index].max_quantity);
        updated[index].quantity = qty;
        setReturnProducts(updated);

        const total = updated.reduce((sum, item) => {
            const itemTotal = item.quantity * (item.sale_price - item.discount_price + item.extra_price);
            return sum + itemTotal;
        }, 0);
        setTotalRefund(total);
    };

    const handleReturnSubmit = async () => {
        const selectedProducts = returnProducts.filter(p => p.quantity > 0);

        if (selectedProducts.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: t("Warning"),
                text: t("Please select at least one product to return"),
            });
            return;
        }

        setReturnSubmitting(true);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/sales/return`,
                {
                    sale_id: parseInt(id),
                    products: selectedProducts.map(p => ({
                        product_id: p.product_id,
                        quantity: p.quantity
                    })),
                    return_reason: returnReason,
                    return_date: returnDate,
                },
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data.status) {
                Swal.fire({
                    icon: 'success',
                    title: t("Success"),
                    text: t(`Return successful! Refund amount: ${formatAmount(response.data.refund_amount)}`),
                });
                setShowReturnModal(false);
                setTimeout(() => fetchSaleData(), 1500);
            } else {
                Swal.fire({
                    icon: 'error',
                    title: t("Error"),
                    text: response.data.message,
                });
            }
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: t("Error"),
                text: err.response?.data?.message || t("Failed to return products"),
            });
        } finally {
            setReturnSubmitting(false);
        }
    };

    // EDIT SALE FUNCTIONS
    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "sale_date" && !canEditSaleDate) return;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
        setSubmitError(null);
    };

    const handleCustomerInputChange = (e) => {
        const { name, value } = e.target;
        setCustomerForm(prev => ({ ...prev, [name]: value }));
        if (customerErrors[name]) setCustomerErrors(prev => ({ ...prev, [name]: "" }));
    };

    const handleCustomerFileChange = (e) => {
        const file = e.target.files[0];
        setCustomerForm(prev => ({ ...prev, img: file }));
        if (customerErrors.img) setCustomerErrors(prev => ({ ...prev, img: "" }));
    };

    const handleAddCustomer = async (e) => {
        e.preventDefault();
        setCustomerSubmitting(true);
        setCustomerErrors({});

        const formDataToSend = new FormData();
        formDataToSend.append('name', customerForm.name);
        formDataToSend.append('email', customerForm.email);
        formDataToSend.append('phone', customerForm.phone);
        if (customerForm.address) formDataToSend.append('address', customerForm.address);
        if (customerForm.img) formDataToSend.append('img', customerForm.img);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/customers`,
                formDataToSend,
                {
                    headers: {
                        Authorization: `Bearer ${AuthToken}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            if (response.data.success) {
                await fetchCustomers();
                const newCustomer = response.data.customer;
                setFormData(prev => ({ ...prev, customer_id: newCustomer.id }));
                setShowCustomerModal(false);
                setCustomerForm({ name: '', email: '', phone: '', address: '', img: null });

                Swal.fire({
                    icon: 'success',
                    title: t('Success'),
                    text: t('Customer created successfully!'),
                    timer: 2000,
                    showConfirmButton: false,
                });
            }
        } catch (error) {
            if (error.response?.status === 422) {
                setCustomerErrors(error.response.data.errors || {});
                Swal.fire({
                    icon: 'warning',
                    title: t('Validation Error'),
                    text: t('Please check the form for errors'),
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: t('Error'),
                    text: t('Failed to create customer'),
                });
            }
        } finally {
            setCustomerSubmitting(false);
        }
    };

    const handlePaymentChange = (index, fieldName, fieldValue) => {
        const updatedPayments = [...formData.payments];

        if (fieldName === "payment_method_id") {
            updatedPayments[index][fieldName] = parseInt(fieldValue);
        } else if (fieldName === "amount") {
            let newAmount = parseFloat(fieldValue) || 0;

            if (!updatedPayments[index].isRefund) {
                const otherPaymentsNetTotal = updatedPayments.reduce((sum, p, i) =>
                    i !== index ? sum + (p.amount || 0) : sum, 0
                );

                const maxAllowed = grandTotal - otherPaymentsNetTotal;

                if (newAmount > maxAllowed && maxAllowed >= 0) {
                    newAmount = maxAllowed;
                    Swal.fire({
                        icon: 'warning',
                        title: t('Adjusted'),
                        text: t('Payment amount adjusted to match grand total'),
                        timer: 2000,
                    });
                }
            }

            updatedPayments[index][fieldName] = newAmount;
        } else if (fieldName === "payment_date") {
            updatedPayments[index][fieldName] = fieldValue;
        }

        setFormData(prev => ({ ...prev, payments: updatedPayments }));
        if (errors[`payments.${index}.${fieldName}`]) {
            setErrors(prev => ({ ...prev, [`payments.${index}.${fieldName}`]: "" }));
        }
        setSubmitError(null);
    };

    const addPaymentRow = () => {
        const remainingForNew = grandTotal - existingPaymentsTotal;
        setFormData(prev => ({
            ...prev,
            payments: [...prev.payments, {
                payment_method_id: "",
                amount: remainingForNew > 0 ? remainingForNew : 0,
                payment_date: getTodayDate(),
                isExisting: false,
                isRefund: false,
            }],
        }));
    };

    const removePaymentRow = (index) => {
        const paymentToRemove = formData.payments[index];
        const updatedPayments = formData.payments.filter((_, i) => i !== index);

        if (paymentToRemove.isExisting && !paymentToRemove.isRefund) {
            setExistingPaymentsTotal(prev => prev - (paymentToRemove.amount || 0));
        }

        setFormData(prev => ({ ...prev, payments: updatedPayments }));
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        setErrors({});
        setSubmitError(null);

        if (!formData.customer_id) {
            Swal.fire({
                icon: 'warning',
                title: t("Validation Error"),
                text: t("Customer is required"),
            });
            setErrors(prev => ({ ...prev, customer_id: "Customer is required" }));
            setSubmitting(false);
            return;
        }

        const netPaymentAmount = formData.payments.reduce((sum, p) => {
            return sum + (Number(p.amount) || 0);
        }, 0);

        if (Math.abs(netPaymentAmount - grandTotal) > 0.01) {
            Swal.fire({
                icon: 'warning',
                title: t("Payment Mismatch"),
                text: t(`Net payment must equal Grand Total (${formatAmount(grandTotal)}). Current Net Payment: ${formatAmount(netPaymentAmount)}`),
            });
            setSubmitting(false);
            return;
        }

        const submitData = {
            customer_id: formData.customer_id,
            sale_date: formData.sale_date,
            notes: Array.isArray(formData.notes) ? formData.notes.join("\n") : formData.notes || "",
            payments: formData.payments
                .filter(p => p.payment_method_id && Number(p.amount) !== 0 && !p.isExisting)
                .map(({ isExisting, isRefund, id, ...payment }) => payment)
        };

        try {
            const response = await axios.put(
                `${process.env.REACT_APP_API_URL}/sales/${id}`,
                submitData,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data.status) {
                Swal.fire({
                    icon: 'success',
                    title: t("Success"),
                    text: t("Sale updated successfully!"),
                    timer: 2000,
                    showConfirmButton: false,
                });
                setTimeout(() => navigate("/sales-list"), 1500);
            } else {
                setSubmitError(response.data.message || "Failed to update sale");
                Swal.fire({
                    icon: 'error',
                    title: t("Error"),
                    text: response.data.message || t("Failed to update sale"),
                });
            }
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors);
                Swal.fire({
                    icon: 'warning',
                    title: t("Validation Error"),
                    text: t("Please check the form for errors"),
                });
            } else {
                setSubmitError(err.response?.data?.message || "An error occurred");
                Swal.fire({
                    icon: 'error',
                    title: t("Error"),
                    text: err.response?.data?.message || t("An error occurred"),
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    // Calculations
    const calculateTotal = (product) => {
        const qty = product.quantity || 0;
        const sale = product.sale_price || 0;
        const discount = product.discount_price || 0;
        const extra = product.extra_price || 0;
        return (qty * sale) - discount + extra;
    };

    const grandTotal = formData.products.reduce((acc, p) => acc + calculateTotal(p), 0);
    const totalReturned = getTotalReturnedAmount();
    const netSoldAmount = getNetSoldAmount();

    const getRemainingAmount = () => {
        const positivePayments = formData.payments
            .filter(p => Number(p.amount) > 0)
            .reduce((sum, p) => sum + Number(p.amount), 0);

        const refundPayments = formData.payments
            .filter(p => Number(p.amount) < 0)
            .reduce((sum, p) => sum + Math.abs(Number(p.amount)), 0);

        const remaining = grandTotal - positivePayments + refundPayments;

        return Math.max(0, remaining);
    };

    useEffect(() => {
        if (netSoldAmount >= 0) {
            setFormData(prev => {
                const updatedPayments = [...prev.payments];
                let currentExistingTotal = 0;

                updatedPayments.forEach(payment => {
                    if (payment.isExisting && !payment.isRefund) {
                        currentExistingTotal += (payment.amount || 0);
                    }
                });

                if (currentExistingTotal !== existingPaymentsTotal) {
                    setExistingPaymentsTotal(currentExistingTotal);
                }

                const remainingForNew = netSoldAmount - currentExistingTotal;

                updatedPayments.forEach((payment, index) => {
                    if (!payment.isExisting && remainingForNew >= 0) {
                        if (payment.amount !== remainingForNew) {
                            updatedPayments[index].amount = remainingForNew;
                        }
                    }
                });

                return { ...prev, payments: updatedPayments };
            });
        }
    }, [netSoldAmount, existingPaymentsTotal]);

    const selectedCustomerValue = customers.find(c => c.id === formData.customer_id);
    const customerSelectValue = selectedCustomerValue ? { value: selectedCustomerValue.id, label: selectedCustomerValue.name } : null;
    const isAddPaymentDisabled = getRemainingAmount() <= 0.01 && getRemainingAmount() >= 0;

    if (loading) return <PageLayout><div className="text-center p-5">{t("Loading...")}</div></PageLayout>;

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <Card className="mb-4 shadow">
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <ButtonComponent
                                    className="btn btn-secondary"
                                    onClick={() => navigate("/sales-list")}
                                    text={t("Back to Sales")}
                                />
                                <div className="d-flex gap-2">
                                    <ButtonComponent
                                        className="btn btn-warning text-white"
                                        onClick={() => setShowExchangeModal(true)}
                                        text={t("Exchange Products")}
                                        disabled={formData.products.length === 0 || getNetSoldAmount() <= 0}
                                    />
                                </div>
                            </div>

                            {submitError && (
                                <Alert variant="danger" className="mb-4" onClose={() => setSubmitError(null)} dismissible>
                                    {submitError}
                                </Alert>
                            )}

                            <Tabs
                                activeKey={activeTab}
                                onSelect={(k) => setActiveTab(k)}
                                className="mb-4"
                            >
                                <Tab eventKey="edit">
                                    {/* Rest of your edit tab content remains the same */}
                                    <Row className="mb-4 mt-3">
                                        <Col xs={12} md={4}>
                                            <Form.Label className="fw-bold">
                                                {t("Customer")} <span className="text-danger">*</span>
                                            </Form.Label>
                                            <div className="d-flex gap-2 align-items-start">
                                                <div className="flex-grow-1">
                                                    <Select
                                                        options={customers.map(c => ({ value: c.id, label: c.name }))}
                                                        value={customerSelectValue}
                                                        onChange={selected => {
                                                            setFormData(prev => ({ ...prev, customer_id: selected ? selected.value : "" }));
                                                            if (errors.customer_id) setErrors(prev => ({ ...prev, customer_id: "" }));
                                                        }}
                                                        isClearable
                                                        placeholder={t("Select Customer")}
                                                        styles={{
                                                            container: provided => ({ ...provided, width: "100%" }),
                                                            control: provided => ({ ...provided, height: "38px" })
                                                        }}
                                                    />
                                                </div>
                                                <ButtonComponent
                                                    type="button"
                                                    className="btn btn-primary"
                                                    onClick={() => setShowCustomerModal(true)}
                                                    title={t("Add New Customer")}
                                                >
                                                    +
                                                </ButtonComponent>
                                            </div>
                                            {errors.customer_id && <Form.Text className="text-danger">{errors.customer_id}</Form.Text>}
                                        </Col>

                                        <Col xs={12} md={4}>
                                            <LabelFieldComponent
                                                type="date"
                                                label={t("Sale Date")}
                                                name="sale_date"
                                                value={formData.sale_date}
                                                onChange={handleChange}
                                                error={errors.sale_date}
                                                required
                                                disabled={!canEditSaleDate}
                                                style={{ width: "100%", backgroundColor: !canEditSaleDate ? "#e9ecef" : "transparent" }}
                                            />
                                            {!canEditSaleDate && (
                                                <Form.Text className="text-muted">
                                                    {t("Sale date cannot be edited")}
                                                </Form.Text>
                                            )}
                                        </Col>
                                        <Col xs={12} md={4}>
                                            <LabelFieldComponent
                                                type="text"
                                                label={t("Sale Number")}
                                                value={formData.sale_number}
                                                disabled
                                                style={{ width: "100%", backgroundColor: !canEditSaleDate ? "#e9ecef" : "transparent" }}
                                            />
                                        </Col>
                                    </Row>

                                    <h4 className="mb-3">{t("Products")}</h4>
                                    <div className="table-responsive">
                                        <Table striped bordered hover className="align-middle">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>{t("Product")}</th>
                                                    <th className="text-center">{t("Quantity")}</th>
                                                    <th className="text-end">{t("Sale Price")}</th>
                                                    <th className="text-end">{t("Discount")}</th>
                                                    <th className="text-end">{t("Extra")}</th>
                                                    <th className="text-end">{t("Total")}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {formData.products.map((prod, index) => {
                                                    const returnedQty = saleReturns
                                                        .filter(ret => ret.product_id === prod.product_id)
                                                        .reduce((sum, ret) => sum + ret.quantity, 0);
                                                    const remainingQty = prod.quantity - returnedQty;

                                                    return (
                                                        <tr key={index}>
                                                            <td className="fw-bold">{prod.product_name}</td>
                                                            <td className="text-center">
                                                                <Badge bg="secondary" pill>{prod.quantity}</Badge>
                                                            </td>
                                                            <td className="text-end">{formatAmount(prod.sale_price)}</td>
                                                            <td className="text-end text-danger">{formatAmount(prod.discount_price)}</td>
                                                            <td className="text-end text-success">{formatAmount(prod.extra_price)}</td>
                                                            <td className="text-end text-primary fw-bold">
                                                                {formatAmount(prod.quantity * prod.sale_price - prod.discount_price + prod.extra_price)}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </div>

                                    {/* Returns Table */}
                                    {saleReturns.length > 0 && (
                                        <>
                                            <h4 className="mb-3 mt-4 text-warning">{t("Returns History")}</h4>
                                            <div className="table-responsive">
                                                <Table striped bordered hover className="align-middle">
                                                    <thead className="table-warning">
                                                        <tr>
                                                            <th>{t("Product")}</th>
                                                            <th className="text-center">{t("Quantity Returned")}</th>
                                                            <th className="text-end">{t("Returned Amount")}</th>
                                                            <th>{t("Reason")}</th>
                                                            <th>{t("Return Date")}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {saleReturns.map((ret, index) => (
                                                            <tr key={index}>
                                                                <td className="fw-bold">{ret.product_name}</td>
                                                                <td className="text-center">
                                                                    <Badge bg="danger" pill>{ret.quantity}</Badge>
                                                                </td>
                                                                <td className="text-end text-danger fw-bold">
                                                                    {formatAmount(ret.total_amount)}
                                                                </td>
                                                                <td>{ret.reason || t("No reason provided")}</td>
                                                                <td>
                                                                    {new Date(ret.return_date).toLocaleString()}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                    <tfoot className="table-danger">
                                                        <tr>
                                                            <td colSpan="2" className="text-end fw-bold">{t("Total Returned Amount")}:</td>
                                                            <td className="text-end fw-bold fs-5">{formatAmount(totalReturned)}</td>
                                                            <td colSpan="2"></td>
                                                        </tr>
                                                    </tfoot>
                                                </Table>
                                            </div>
                                        </>
                                    )}

                                    <Row className="mb-4 mt-3">
                                        <Col className="text-end">
                                            <div className="p-3 bg-light rounded">
                                                <div className="mb-2">
                                                    <strong>{t("Original Grand Total")}: </strong>
                                                    <span className="text-primary fw-bold">{formatAmount(grandTotal)}</span>
                                                </div>
                                                {totalReturned > 0 && (
                                                    <>
                                                        <div className="mb-2">
                                                            <strong>{t("Total Returned")}: </strong>
                                                            <span className="text-danger fw-bold">{formatAmount(totalReturned)}</span>
                                                        </div>
                                                    </>
                                                )}
                                                <hr />
                                                <div>
                                                    <strong>{t("Remaining Balance")}: </strong>
                                                    <span className={`fs-4 fw-bold ${getRemainingAmount() === 0 ? 'text-success' : 'text-warning'}`}>
                                                        {formatAmount(getRemainingAmount())}
                                                    </span>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>

                                    <Row className="mb-4">
                                        <Col xl={6}>
                                            <LabelTextareaComponent
                                                label={t("Notes")}
                                                name="notes"
                                                value={
                                                    Array.isArray(formData.notes)
                                                        ? formData.notes.join("\n")
                                                        : typeof formData.notes === "string"
                                                            ? formData.notes
                                                            : ""
                                                }
                                                onChange={handleChange}
                                                rows={4}
                                                style={{ width: "100%" }}
                                            />
                                        </Col>

                                        <Col xl={6}>
                                            <h5 className="mb-3">{t("Payments")}</h5>

                                            {formData.payments.length === 0 && (
                                                <Alert variant="info">No payments recorded</Alert>
                                            )}

                                            {/* ===== PARENT PAYMENTS ===== */}
                                            {formData.payments.map((pay, index) => (
                                                <Card key={`parent-${index}`} className={`mb-3 shadow-sm ${pay.isRefund ? 'border-danger' : 'border-success'}`}>
                                                    <Card.Body>
                                                        {/* Parent Payment */}
                                                        <Row className="align-items-center">
                                                            <Col xs={12} md={4} className="mb-2 mb-md-0">
                                                                <Form.Label className="fw-bold mb-1">{pay.isRefund ? t("Refund Method") : t("Payment Method")}</Form.Label>
                                                                <select
                                                                    className="form-select form-select-sm"
                                                                    value={pay.payment_method_id}
                                                                    onChange={e => handlePaymentChange(index, 'payment_method_id', e.target.value)}
                                                                    disabled={pay.isExisting}
                                                                >
                                                                    <option value="">{t("Select Payment Method")}</option>
                                                                    {paymentMethods.map(pm => (
                                                                        <option key={pm.id} value={pm.id}>
                                                                            {pm.name}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </Col>
                                                            <Col xs={12} md={3} className="mb-2 mb-md-0">
                                                                <Form.Label className="fw-bold mb-1">{pay.isRefund ? t("Refund Amount") : t("Amount")}</Form.Label>
                                                                <input
                                                                    type="number"
                                                                    className="form-control form-control-sm"
                                                                    step="0.01"
                                                                    value={pay.amount}
                                                                    onChange={e => handlePaymentChange(index, 'amount', e.target.value)}
                                                                    disabled={pay.isExisting}
                                                                    style={{
                                                                        color: pay.amount < 0 ? '#dc3545' : '#198754',
                                                                        fontWeight: 'bold'
                                                                    }}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={3} className="mb-2 mb-md-0">
                                                                <Form.Label className="fw-bold mb-1">{t("Payment Date")}</Form.Label>
                                                                <input
                                                                    type="date"
                                                                    className="form-control form-control-sm"
                                                                    value={pay.payment_date || getTodayDate()}
                                                                    onChange={e => handlePaymentChange(index, 'payment_date', e.target.value)}
                                                                    disabled={pay.isExisting}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={2} className="text-center">
                                                                {!pay.isExisting && (
                                                                    <ButtonComponent
                                                                        className="btn btn-danger btn-sm mt-3 mt-md-0"
                                                                        onClick={() => removePaymentRow(index)}
                                                                        text="Remove"
                                                                    />
                                                                )}
                                                            </Col>
                                                        </Row>

                                                        {/* Parent Payment Badge - Check if this payment itself is adjusted from a sale */}
                                                        {pay.isExisting && (
                                                            <div className="mt-2">
                                                                <Badge bg={pay.isRefund ? "danger" : "info"}>
                                                                    {pay.isRefund ? "Refund payment (cannot be modified)" : "Existing payment (cannot be modified)"}
                                                                </Badge>
                                                            </div>
                                                        )}

                                                        {/* Show if this parent payment is adjusted from a sale (self payment) */}
                                                        {pay.parent_payment && pay.parent_payment.model && (
                                                            <div className="mt-2">
                                                                <small className="text-muted">
                                                                    <i className="bi bi-arrow-right me-1"></i>
                                                                    <span className="fw-semibold">Adjust Payment from Sale No: {pay.parent_payment.model.sale_number || 'N/A'}</span>
                                                                </small>
                                                            </div>
                                                        )}
                                                    </Card.Body>
                                                </Card>
                                            ))}

                                            {/* ===== ALL CHILD PAYMENTS - Simple lines ===== */}
                                            {formData.payments.some(pay => pay.child_payments && pay.child_payments.length > 0) && (
                                                <div className="mt-3">
                                                    <hr className="my-3" />
                                                    <h6 className="text-muted mb-3">
                                                        <i className="bi bi-arrow-return-right me-2"></i>
                                                        {t("Adjusted Payments from Sales")}
                                                        <Badge bg="secondary" className="ms-2">
                                                            {formData.payments.reduce((total, pay) => total + (pay.child_payments?.length || 0), 0)}
                                                        </Badge>
                                                    </h6>

                                                    {/* Show all child payments as simple lines */}
                                                    {formData.payments.map((pay, parentIndex) =>
                                                        pay.child_payments && pay.child_payments.map((child, childIndex) => (
                                                            <div
                                                                key={`child-${parentIndex}-${childIndex}`}
                                                                className="mb-1 p-2 bg-light rounded d-flex justify-content-between align-items-center"
                                                                style={{ borderLeft: `3px solid ${child.amount < 0 ? '#dc3545' : '#198754'}` }}
                                                            >
                                                                <div>
                                                                    <span className="fw-semibold">
                                                                        Adjust Payment in Sale No: {child.model?.sale_number || 'N/A'}
                                                                    </span>

                                                                </div>
                                                                <div>
                                                                    <Badge bg={child.amount < 0 ? 'danger' : 'success'}>
                                                                        {child.amount < 0 ? '-' : ''}{Math.abs(child.amount).toFixed(2)}
                                                                    </Badge>
                                                                    <small className="text-muted ms-2">
                                                                        {child.payment_date ? new Date(child.payment_date).toLocaleDateString() : ''}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            )}

                                            <div className="d-grid gap-2 mt-3">
                                                <ButtonComponent
                                                    className="btn btn-primary"
                                                    onClick={addPaymentRow}
                                                    text={t("+ Add Payment")}
                                                    disabled={isAddPaymentDisabled}
                                                />
                                            </div>
                                        </Col>
                                    </Row>

                                    <div className="d-grid gap-2">
                                        <ButtonComponent
                                            className="btn btn-primary btn-lg"
                                            onClick={handleSubmit}
                                            text={submitting ? t("Updating...") : t("Update Sale")}
                                            disabled={submitting}
                                        />
                                    </div>
                                </Tab>
                            </Tabs>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Exchange Modal Component */}
            <ExchangeModal
                show={showExchangeModal}
                onHide={() => setShowExchangeModal(false)}
                saleId={id}
                saleProducts={formData.products}
                saleReturns={saleReturns}
                availableProducts={availableProducts}
                paymentMethods={paymentMethods}
                authToken={AuthToken}
                onSuccess={() => {
                    fetchSaleData();
                    setShowExchangeModal(false);
                }}
                t={t}
            />

            {/* Add Customer Modal */}
            <Modal show={showCustomerModal} onHide={() => setShowCustomerModal(false)} size="md" centered>
                <Modal.Header closeButton className="bg-primary">
                    <Modal.Title className="text-white">{t("Add New Customer")}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleAddCustomer}>
                    <Modal.Body>
                        {customerErrors.general && (
                            <Alert variant="danger">{customerErrors.general}</Alert>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">{t("Name")} <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={customerForm.name}
                                onChange={handleCustomerInputChange}
                                isInvalid={!!customerErrors.name}
                            />
                            <Form.Control.Feedback type="invalid">{customerErrors.name}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">{t("Email")} <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                value={customerForm.email}
                                onChange={handleCustomerInputChange}
                                isInvalid={!!customerErrors.email}
                            />
                            <Form.Control.Feedback type="invalid">{customerErrors.email}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">{t("Phone")} <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                name="phone"
                                value={customerForm.phone}
                                onChange={handleCustomerInputChange}
                                isInvalid={!!customerErrors.phone}
                            />
                            <Form.Control.Feedback type="invalid">{customerErrors.phone}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">{t("Address")}</Form.Label>
                            <Form.Control
                                as="textarea"
                                name="address"
                                rows={2}
                                value={customerForm.address}
                                onChange={handleCustomerInputChange}
                                isInvalid={!!customerErrors.address}
                            />
                            <Form.Control.Feedback type="invalid">{customerErrors.address}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">{t("Profile Image")}</Form.Label>
                            <Form.Control
                                type="file"
                                name="img"
                                accept="image/*"
                                onChange={handleCustomerFileChange}
                                isInvalid={!!customerErrors.img}
                            />
                            <Form.Control.Feedback type="invalid">{customerErrors.img}</Form.Control.Feedback>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <ButtonComponent
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setShowCustomerModal(false)}
                            text={t("Cancel")}
                        />
                        <ButtonComponent
                            type="submit"
                            className="btn btn-primary"
                            disabled={customerSubmitting}
                            text={customerSubmitting ? t("Creating...") : t("Create Customer")}
                        />
                    </Modal.Footer>
                </Form>
            </Modal>
        </PageLayout>
    );
}